/*
 * AIH Studio — interaction layer
 * Dependency-free. Every interactive component is a custom element, so it
 * initialises itself when Shopify's theme editor re-renders a section.
 * Motion is gated by html.motion (set in <head> only when the merchant has
 * animations enabled AND the visitor hasn't asked for reduced motion).
 */
(() => {
  'use strict';

  const d = document;
  const root = d.documentElement;
  const theme = window.theme || { routes: {}, strings: {} };
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const motionOK = () => root.classList.contains('motion') && !reduceMQ.matches;
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const raf = (fn) => window.requestAnimationFrame(fn);

  /* ------------------------------------------------------------------ */
  /* Utilities                                                           */
  /* ------------------------------------------------------------------ */
  const live = (msg) => {
    const region = d.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    setTimeout(() => { region.textContent = msg; }, 60);
  };

  const decodeEntities = (str) => {
    const el = d.createElement('textarea');
    el.innerHTML = String(str).replace(/<[^>]*>/g, '');
    return el.value;
  };

  const formatMoney = (cents, format) => {
    const fmt = format || theme.moneyFormat || '${{amount}}';
    const value = typeof cents === 'string' ? parseInt(cents.replace('.', ''), 10) : cents;
    const delimit = (num, precision = 2, thousands = ',', decimal = '.') => {
      if (num == null || Number.isNaN(num)) return '0';
      const fixed = (num / 100).toFixed(precision).split('.');
      const whole = fixed[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return whole + (fixed[1] ? decimal + fixed[1] : '');
    };
    const out = fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return delimit(value, 0);
        case 'amount_with_comma_separator': return delimit(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delimit(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delimit(value, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return delimit(value, 0, ' ');
        case 'amount_with_space_separator': return delimit(value, 2, ' ', ',');
        case 'amount_with_period_and_space_separator': return delimit(value, 2, ' ', '.');
        default: return delimit(value, 2);
      }
    });
    return decodeEntities(out);
  };

  const fetchJSON = async (url, options = {}) => {
    const controller = 'AbortController' in window ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), 15000) : null;
    try {
      const res = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller ? controller.signal : undefined,
        ...options,
      });
      let data = null;
      try { data = await res.json(); } catch (e) { /* non-JSON response */ }
      if (!res.ok) {
        const message = (data && (data.description || data.message)) || strings.error;
        throw new Error(typeof message === 'string' ? message : strings.error);
      }
      return data;
    } catch (err) {
      if (err && err.name === 'AbortError') throw new Error(strings.error);
      throw err;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };
  const postJSON = (url, body) => fetchJSON(url, { method: 'POST', body: JSON.stringify(body) });

  const scrollBehavior = () => (reduceMQ.matches ? 'auto' : 'smooth');

  /* ------------------------------------------------------------------ */
  /* Reveal system + split headings                                      */
  /* ------------------------------------------------------------------ */
  const splitHeading = (el) => {
    if (el.dataset.splitDone) return;
    el.dataset.splitDone = '1';
    if (!motionOK()) return;
    const label = el.textContent.replace(/\s+/g, ' ').trim();
    if (!label) return;
    let wi = 0;
    const visual = d.createElement('span');
    visual.setAttribute('aria-hidden', 'true');
    const walk = (source, target) => {
      source.childNodes.forEach((node) => {
        if (node.nodeType === 3) {
          node.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { target.appendChild(d.createTextNode(' ')); return; }
            const w = d.createElement('span');
            w.className = 'split__w';
            const i = d.createElement('span');
            i.className = 'split__i';
            i.style.setProperty('--wi', wi++);
            i.textContent = part;
            w.appendChild(i);
            target.appendChild(w);
          });
        } else if (node.nodeType === 1) {
          const clone = node.cloneNode(false);
          target.appendChild(clone);
          walk(node, clone);
        }
      });
    };
    walk(el, visual);
    const sr = d.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = label;
    el.textContent = '';
    el.append(sr, visual);
  };

  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -7% 0px', threshold: 0 })
    : null;

  // Elements in the last few pixels of the page can never cross the observer's
  // inset line, so reveal whatever is left once the visitor reaches the bottom.
  window.addEventListener('scroll', () => {
    if (window.innerHeight + window.scrollY >= d.documentElement.scrollHeight - 6) {
      d.querySelectorAll('[data-reveal]:not(.is-in), [data-split]:not(.is-in)').forEach((el) => el.classList.add('is-in'));
    }
  }, { passive: true });

  const initReveal = (scope = d) => {
    scope.querySelectorAll('[data-split]').forEach(splitHeading);
    const targets = scope.querySelectorAll('[data-reveal]:not(.is-in), [data-split]:not(.is-in), .levels:not(.is-in), .honest__board:not(.is-in)');
    const vh = window.innerHeight;
    targets.forEach((el) => {
      if (!motionOK() || !revealObserver || theme.designMode) { el.classList.add('is-in'); return; }
      // Anything already on screen at load animates in straight away (never stuck at the fold edge).
      if (scope === d && el.getBoundingClientRect().top < vh) { raf(() => raf(() => el.classList.add('is-in'))); return; }
      revealObserver.observe(el);
    });
  };

  /* ------------------------------------------------------------------ */
  /* Parallax                                                            */
  /* ------------------------------------------------------------------ */
  const Parallax = (() => {
    const visible = new Set();
    let ticking = false;
    const enabled = () => theme.parallax && motionOK();
    const io = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
      schedule();
    }, { rootMargin: '10% 0px' }) : null;
    const update = () => {
      ticking = false;
      const vh = window.innerHeight;
      visible.forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        const progress = clamp((r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2), -1, 1);
        el.style.setProperty('--py', `${(-progress * r.height * 0.055).toFixed(1)}px`);
      });
    };
    const schedule = () => {
      if (!ticking && visible.size) { ticking = true; raf(update); }
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    return {
      observe(scope = d) {
        if (!enabled() || !io) return;
        scope.querySelectorAll('[data-parallax]').forEach((el) => io.observe(el));
      },
    };
  })();

  /* ------------------------------------------------------------------ */
  /* In-view video playback                                              */
  /* ------------------------------------------------------------------ */
  const videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const v = entry.target;
      if (entry.isIntersecting && !reduceMQ.matches) {
        if (v.preload === 'none') v.preload = 'auto';
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } else {
        v.pause();
      }
    });
  }, { threshold: 0.25 }) : null;
  const initVideos = (scope = d) => {
    scope.querySelectorAll('video.slot__video').forEach((v) => {
      v.muted = true;
      v.setAttribute('aria-hidden', 'true');
      if (videoObserver) videoObserver.observe(v);
    });
  };

  /* ------------------------------------------------------------------ */
  /* Cart count                                                          */
  /* ------------------------------------------------------------------ */
  const updateCartCount = async () => {
    try {
      const cart = await fetchJSON(routes.cartJs);
      d.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
        el.classList.remove('is-bump');
        void el.offsetWidth;
        el.classList.add('is-bump');
      });
      d.querySelectorAll('[data-cart-label]').forEach((el) => {
        el.textContent = el.textContent.replace(/\d+/, cart.item_count);
      });
      return cart;
    } catch (e) {
      return null;
    }
  };

  /* ------------------------------------------------------------------ */
  /* <site-header>                                                       */
  /* ------------------------------------------------------------------ */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.wrap = this.closest('.section-header') || this;
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.lastY = window.scrollY;
      this.ticking = false;
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        raf(() => this.update());
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('focusin', () => this.show());
      this.update();
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    show() {
      this.wrap.classList.remove('is-hidden');
      root.style.setProperty('--hdr-offset', 'var(--hdr-h)');
    }
    hide() {
      this.wrap.classList.add('is-hidden');
      root.style.setProperty('--hdr-offset', '0px');
    }
    update() {
      this.ticking = false;
      const y = Math.max(0, window.scrollY);
      this.classList.toggle('is-scrolled', y > 8);
      if (this.hideOnScroll && !d.body.classList.contains('is-locked')) {
        if (y > this.lastY + 6 && y > 240) this.hide();
        else if (y < this.lastY - 6 || y < 120) this.show();
      }
      this.lastY = y;
    }
  }

  /* ------------------------------------------------------------------ */
  /* <menu-drawer>                                                       */
  /* ------------------------------------------------------------------ */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.toggleBtn = d.querySelector(`[data-menu-toggle][aria-controls="${this.id}"]`);
      this.panel = this.querySelector('.drawer__panel');
      this.onToggle = () => (this.isOpen ? this.close() : this.open());
      if (this.toggleBtn) this.toggleBtn.addEventListener('click', this.onToggle);
      this.querySelectorAll('[data-menu-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.querySelectorAll('[data-menu-link]').forEach((a) => a.addEventListener('click', () => {
        if (a.getAttribute('href').includes('#')) this.close(false);
      }));
      this.addEventListener('keydown', (e) => this.onKey(e));
      this.isOpen = false;
    }
    disconnectedCallback() {
      if (this.toggleBtn) this.toggleBtn.removeEventListener('click', this.onToggle);
      d.body.classList.remove('is-locked');
    }
    focusables() {
      return [...this.panel.querySelectorAll('a[href], button:not([disabled]), select, input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null || el === d.activeElement);
    }
    open() {
      this.isOpen = true;
      this.hidden = false;
      d.body.classList.add('is-locked');
      this.toggleBtn && this.toggleBtn.setAttribute('aria-expanded', 'true');
      raf(() => raf(() => {
        this.classList.add('is-open');
        const first = this.focusables()[0];
        (first || this.panel).focus({ preventScroll: true });
      }));
    }
    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      d.body.classList.remove('is-locked');
      if (this.toggleBtn) {
        this.toggleBtn.setAttribute('aria-expanded', 'false');
        if (restoreFocus) this.toggleBtn.focus({ preventScroll: true });
      }
      const done = () => { if (!this.isOpen) this.hidden = true; };
      if (motionOK()) setTimeout(done, 560); else done();
    }
    onKey(e) {
      if (!this.isOpen) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
      if (e.key !== 'Tab') return;
      const items = this.focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && d.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && d.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  /* ------------------------------------------------------------------ */
  /* <product-buy> — AJAX add + straight to checkout                     */
  /* ------------------------------------------------------------------ */
    /* Digital-download consent: one shared state for every checkbox on the page. */
  const Consent = (() => {
    const KEY = 'Digital download consent';
    let agreed = false;
    let agreedAt = '';
    const inputs = () => [...d.querySelectorAll('[data-consent-input]')];
    const textOf = (input) => (input && input.value) || '';
    const clearInvalid = (input) => {
      const wrap = input.closest('[data-consent]');
      if (!wrap) return;
      wrap.classList.remove('is-invalid');
      const err = wrap.querySelector('[data-consent-error]');
      if (err) err.hidden = true;
      input.removeAttribute('aria-invalid');
    };
    const showInvalid = (input) => {
      const wrap = input.closest('[data-consent]');
      if (!wrap) return;
      wrap.classList.remove('is-invalid');
      void wrap.offsetWidth;
      wrap.classList.add('is-invalid');
      const err = wrap.querySelector('[data-consent-error]');
      if (err) { err.hidden = false; live(err.textContent); }
      input.setAttribute('aria-invalid', 'true');
      input.focus({ preventScroll: true });
      const r = wrap.getBoundingClientRect();
      if (r.top < 80 || r.bottom > window.innerHeight - 100) wrap.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
    };
    const set = (value, source) => {
      agreed = value;
      agreedAt = value ? new Date().toISOString() : '';
      inputs().forEach((input) => {
        if (input !== source) input.checked = value;
        clearInvalid(input);
        // Order-level records (cart page) also carry the time of agreement.
        if (/^attributes\[/.test(input.name || '')) {
          const base = input.dataset.baseValue || input.value;
          input.dataset.baseValue = base;
          input.value = value ? `Agreed ${agreedAt} | ${base}` : base;
        }
      });
    };
    d.addEventListener('change', (e) => {
      if (e.target.matches && e.target.matches('[data-consent-input]')) set(e.target.checked, e.target);
    });
    // Consent must be given fresh: never accept a box the browser re-ticked when restoring the page.
    const resetAll = () => set(false, null);
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', resetAll); else resetAll();
    window.addEventListener('pageshow', resetAll);
    // Styled message instead of the browser's bubble when a required box blocks a form (cart page).
    d.addEventListener('invalid', (e) => {
      if (e.target.matches && e.target.matches('[data-consent-input]')) { e.preventDefault(); showInvalid(e.target); }
    }, true);

    const dialog = () => d.querySelector('[data-consent-dialog]');
    const ask = (trigger) => new Promise((resolve) => {
      const dlg = dialog();
      if (!dlg || typeof dlg.showModal !== 'function') { resolve(false); return; }
      const input = dlg.querySelector('[data-consent-input]');
      const confirmBtn = dlg.querySelector('[data-consent-confirm]');
      let done = false;
      const finish = (ok) => {
        if (done) return;
        done = true;
        confirmBtn.removeEventListener('click', onConfirm);
        dlg.removeEventListener('close', onClose);
        dlg.removeEventListener('click', onBackdrop);
        d.body.classList.remove('is-locked');
        if (!ok && trigger) trigger.focus({ preventScroll: true });
        resolve(ok);
      };
      const onConfirm = (e) => {
        if (!input.checked) { e.preventDefault(); showInvalid(input); return; }
        e.preventDefault();
        dlg.close('confirm');
        finish(true);
      };
      const onClose = () => finish(false);
      const onBackdrop = (e) => { if (e.target === dlg) dlg.close('cancel'); };
      confirmBtn.addEventListener('click', onConfirm);
      dlg.addEventListener('close', onClose);
      dlg.addEventListener('click', onBackdrop);
      clearInvalid(input);
      d.body.classList.add('is-locked');
      dlg.showModal();
      input.focus({ preventScroll: true });
    });

    return {
      key: KEY,
      /** Resolves true once the visitor has agreed; shows the inline error or the pop-up otherwise. */
      async ensure(buyEl) {
        const inline = buyEl.querySelector('[data-consent-input]');
        if (inline && inline.checked) { if (!agreed) set(true, inline); return true; }
        if (agreed && !inline) return true;
        if (inline) { showInvalid(inline); return false; }
        const ok = await ask(buyEl.querySelector('[data-buy-button]'));
        if (ok && !agreed) set(true, null);
        return ok;
      },
      text() { return textOf(inputs()[0]); },
      stamp() { return agreedAt || new Date().toISOString(); },
    };
  })();

  class ProductBuy extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.btn = this.querySelector('[data-buy-button]');
      this.err = this.querySelector('[data-buy-error]');
      this.label = this.querySelector('[data-label]');
      this.behaviour = this.dataset.behaviour || 'checkout';
      if (!this.form || !this.btn) return;
      // The theme shows its own messages; native validation is only the no-JavaScript fallback.
      this.form.noValidate = true;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    get variantId() {
      const input = this.form.querySelector('[name="id"]');
      return input ? Number(input.value) : null;
    }
    setBusy(busy) {
      this.busy = busy;
      this.btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (busy) this.btn.setAttribute('aria-disabled', 'true');
      else this.btn.removeAttribute('aria-disabled');
    }
    showError(message) {
      if (!this.err) return;
      this.err.textContent = message || strings.error;
      this.err.hidden = false;
    }
    clearError() {
      if (this.err) { this.err.hidden = true; this.err.textContent = ''; }
    }
    reset() { this.setBusy(false); }
    async onSubmit(e) {
      e.preventDefault();
      if (this.busy || this.btn.disabled) return;
      const id = this.variantId;
      if (!id) { this.showError(strings.unavailable); return; }
      this.clearError();
      if (!(await Consent.ensure(this))) return;
      this.setBusy(true);
      live(this.behaviour === 'checkout' ? strings.redirecting : strings.adding);
      const consentText = Consent.text();
      const item = { id, quantity: 1, properties: { [Consent.key]: consentText } };
      const attributes = { [Consent.key]: `Agreed ${Consent.stamp()} | ${consentText}` };
      const cartUpdate = `${routes.root || '/'}cart/update.js`;
      try {
        if (this.behaviour === 'checkout') {
          // Keep exactly one copy of the guide in the cart, carrying the consent record, then hand off to checkout.
          const cart = await fetchJSON(routes.cartJs);
          const updates = {};
          cart.items.forEach((line) => { if (line.variant_id === id) updates[line.key] = 0; });
          await postJSON(cartUpdate, { updates, attributes });
          await postJSON(routes.cartAdd, { items: [item] });
          window.location.assign(routes.checkout || '/checkout');
        } else {
          await postJSON(routes.cartAdd, { items: [item] });
          await postJSON(cartUpdate, { attributes });
          live(strings.added);
          await updateCartCount();
          window.location.assign(routes.cart || '/cart');
        }
      } catch (err) {
        this.setBusy(false);
        this.showError(err && err.message ? err.message : strings.error);
        live(err && err.message ? err.message : strings.error);
      }
    }
  }
  // Coming back from checkout via the back button restores a cached page: un-stick spinners.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) d.querySelectorAll('product-buy').forEach((el) => el.reset && el.reset());
  });

  /* ------------------------------------------------------------------ */
  /* <sticky-buy>                                                        */
  /* ------------------------------------------------------------------ */
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      this.hidden = false;
      this.inert = true;
      this.setAttribute('aria-hidden', 'true');
      this.inView = new Set();
      const anchors = [...d.querySelectorAll('[data-buy-anchor], [data-buy-hide]')];
      this.hasAnchors = d.querySelectorAll('[data-buy-anchor]').length > 0;
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? this.inView.add(e.target) : this.inView.delete(e.target)));
        this.update();
      }, { threshold: 0 });
      anchors.forEach((a) => this.io.observe(a));
      this.onScroll = () => this.update();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }
    update() {
      let pastTop = window.scrollY > (this.hasAnchors ? 40 : 480);
      if (!pastTop && this.hasAnchors) {
        // On very short screens the hero button can start below the fold: offer the bar straight away.
        const first = d.querySelector('[data-buy-anchor]');
        pastTop = !!first && first.getBoundingClientRect().top > window.innerHeight;
      }
      const visible = pastTop && this.inView.size === 0;
      if (visible === this.visible) return;
      this.visible = visible;
      this.classList.toggle('is-visible', visible);
      this.inert = !visible;
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }
  }

  /* ------------------------------------------------------------------ */
  /* <scene-deck> — the hero's "same face, every scene" deck             */
  /* ------------------------------------------------------------------ */
  class SceneDeck extends HTMLElement {
    connectedCallback() {
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.n = this.cards.length;
      if (this.n < 1) return;
      this.index = 0;
      this.bars = [...this.querySelectorAll('[data-goto]')];
      this.stage = this.querySelector('[data-deck-cards]');
      this.caption = this.querySelector('[data-caption]');
      this.capTitle = this.querySelector('[data-caption-title]');
      this.capDetail = this.querySelector('[data-caption-detail]');
      this.countEl = this.querySelector('[data-count-current]');
      this.playBtn = this.querySelector('[data-toggle-play]');
      this.interval = Number(this.dataset.interval) || 4000;
      this.autoplay = this.dataset.autoplay === 'true' && motionOK() && this.n > 1;
      this.pauses = new Set();
      this.remaining = this.interval;

      this.layout();
      this.querySelector('[data-prev]')?.addEventListener('click', () => { this.userAct(); this.go(-1); });
      this.querySelector('[data-next]')?.addEventListener('click', () => { this.userAct(); this.go(1); });
      this.bars.forEach((b) => b.addEventListener('click', () => {
        this.userAct();
        const to = Number(b.dataset.goto);
        this.goTo(to, to >= this.index ? 1 : -1);
      }));
      this.playBtn?.addEventListener('click', () => this.togglePlay());
      this.stage.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.userAct(); this.go(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.userAct(); this.go(-1); }
      });
      this.bindPointer();

      if (this.autoplay) {
        this.classList.add('is-playing');
        this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.pause('hover'); });
        this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.resume('hover'); });
        this.addEventListener('focusin', () => this.pause('focus'));
        this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.resume('focus'); });
        this.visIO = new IntersectionObserver(([entry]) => (entry.isIntersecting ? this.resume('offscreen') : this.pause('offscreen')), { threshold: 0.2 });
        this.visIO.observe(this);
        this.onVis = () => (d.hidden ? this.pause('tab') : this.resume('tab'));
        d.addEventListener('visibilitychange', this.onVis);
        this.startTimer(this.interval);
      } else if (this.playBtn) {
        this.playBtn.hidden = true;
      }
      this.updateUI(false);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.visIO) this.visIO.disconnect();
      if (this.onVis) d.removeEventListener('visibilitychange', this.onVis);
    }
    layout() {
      this.cards.forEach((card, i) => {
        const rel = (i - this.index + this.n) % this.n;
        card.dataset.pos = String(Math.min(rel, 3));
        card.setAttribute('aria-hidden', rel === 0 ? 'false' : 'true');
      });
    }
    go(dir) { this.goTo((this.index + dir + this.n) % this.n, dir); }
    goTo(to, dir = 1) {
      if (to === this.index) return;
      const leaving = this.cards[this.index];
      this.index = to;
      if (motionOK()) {
        leaving.classList.remove('is-dragging');
        leaving.style.removeProperty('--tx');
        leaving.style.removeProperty('--rot');
        leaving.classList.add('is-leaving');
        leaving.classList.toggle('is-leaving-right', dir < 0);
        clearTimeout(leaving._t);
        leaving._t = setTimeout(() => leaving.classList.remove('is-leaving', 'is-leaving-right'), 640);
      }
      this.layout();
      this.updateUI(true);
      if (this.autoplay && !this.userPaused) this.startTimer(this.interval);
    }
    updateUI(animate) {
      this.bars.forEach((b, i) => {
        b.classList.toggle('is-done', i < this.index);
        b.classList.remove('is-active');
        if (i === this.index) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
      });
      const active = this.bars[this.index];
      if (active) { void active.offsetWidth; active.classList.add('is-active'); }
      const card = this.cards[this.index];
      if (this.capTitle) this.capTitle.textContent = card.dataset.title || '';
      if (this.capDetail) this.capDetail.textContent = card.dataset.detail || '';
      if (this.countEl) this.countEl.textContent = String(this.index + 1).padStart(2, '0');
      if (animate && this.caption && motionOK()) {
        this.caption.classList.remove('is-swap');
        void this.caption.offsetWidth;
        this.caption.classList.add('is-swap');
      }
    }
    startTimer(ms) {
      clearTimeout(this.timer);
      this.remaining = ms;
      this.startedAt = performance.now();
      if (this.pauses.size || this.userPaused) return;
      this.timer = setTimeout(() => this.go(1), ms);
    }
    pause(reason) {
      if (!this.autoplay) return;
      const wasRunning = !this.pauses.size && !this.userPaused;
      this.pauses.add(reason);
      if (wasRunning) {
        clearTimeout(this.timer);
        this.remaining = Math.max(200, this.remaining - (performance.now() - this.startedAt));
      }
      this.classList.add('is-paused');
    }
    resume(reason) {
      if (!this.autoplay) return;
      this.pauses.delete(reason);
      if (this.pauses.size || this.userPaused) return;
      this.classList.remove('is-paused');
      this.startedAt = performance.now();
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.go(1), this.remaining);
    }
    togglePlay() {
      this.userPaused = !this.userPaused;
      this.classList.toggle('is-user-paused', this.userPaused);
      this.playBtn.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
      const label = this.playBtn.querySelector('[data-play-label]');
      if (label) label.textContent = this.userPaused ? label.dataset.labelPlay : label.dataset.labelPause;
      if (this.userPaused) {
        clearTimeout(this.timer);
        this.remaining = Math.max(200, this.remaining - (performance.now() - this.startedAt));
        this.classList.add('is-paused');
      } else {
        this.classList.remove('is-paused');
        this.resume('user');
      }
    }
    userAct() {
      // Once a visitor takes control, announce changes politely.
      if (this.caption) this.caption.setAttribute('aria-live', 'polite');
    }
    bindPointer() {
      let startX = 0; let startY = 0; let dx = 0; let t0 = 0; let tracking = false; let dragging = false; let pid = null;
      const front = () => this.cards[this.index];
      this.stage.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        tracking = true; dragging = false; dx = 0; pid = e.pointerId;
        startX = e.clientX; startY = e.clientY; t0 = performance.now();
      });
      this.stage.addEventListener('pointermove', (e) => {
        if (!tracking || e.pointerId !== pid) return;
        const mx = e.clientX - startX;
        const my = e.clientY - startY;
        if (!dragging && Math.abs(mx) > 8 && Math.abs(mx) > Math.abs(my)) {
          dragging = true;
          this.stage.setPointerCapture(pid);
          front().classList.add('is-dragging');
          this.pause('drag');
        }
        if (dragging) {
          dx = mx;
          const card = front();
          card.style.setProperty('--tx', `${dx}px`);
          card.style.setProperty('--rot', `${dx / 22}deg`);
        }
      });
      const end = (e) => {
        if (!tracking || (e && e.pointerId !== pid)) return;
        tracking = false;
        const card = front();
        const dt = Math.max(1, performance.now() - t0);
        if (dragging) {
          card.classList.remove('is-dragging');
          const velocity = Math.abs(dx) / dt;
          this.resume('drag');
          if (Math.abs(dx) > 64 || velocity > 0.55) {
            this.userAct();
            this.go(dx < 0 ? 1 : -1);
          } else {
            card.style.removeProperty('--tx');
            card.style.removeProperty('--rot');
          }
        } else if (e && e.type === 'pointerup' && dt < 400) {
          // Tap: right side advances, left side goes back (Stories convention).
          const r = this.stage.getBoundingClientRect();
          this.userAct();
          this.go(e.clientX - r.left > r.width * 0.4 ? 1 : -1);
        }
        dragging = false;
      };
      this.stage.addEventListener('pointerup', end);
      this.stage.addEventListener('pointercancel', (e) => {
        if (dragging) { const c = front(); c.classList.remove('is-dragging'); c.style.removeProperty('--tx'); c.style.removeProperty('--rot'); this.resume('drag'); }
        tracking = false; dragging = false;
        void e;
      });
    }
    selectBlock(blockEl) {
      const i = this.cards.findIndex((c) => c === blockEl || c.contains(blockEl));
      if (i >= 0) { this.pause('editor'); this.goTo(i, i >= this.index ? 1 : -1); }
    }
  }

  /* ------------------------------------------------------------------ */
  /* <ih-carousel>                                                       */
  /* ------------------------------------------------------------------ */
  class IhCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.bar = this.querySelector('[data-progress]');
      this.onScroll = () => { if (!this.ticking) { this.ticking = true; raf(() => this.update()); } };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.prevBtn?.addEventListener('click', () => this.step(-1));
      this.nextBtn?.addEventListener('click', () => this.step(1));
      this.bindDrag();
      this.update();
    }
    disconnectedCallback() { window.removeEventListener('resize', this.onScroll); }
    update() {
      this.ticking = false;
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      const progress = t.scrollWidth ? clamp((t.scrollLeft + t.clientWidth) / t.scrollWidth, 0, 1) : 1;
      if (this.bar) this.bar.style.setProperty('--progress', progress.toFixed(3));
      if (this.prevBtn) this.prevBtn.disabled = t.scrollLeft <= 2;
      if (this.nextBtn) this.nextBtn.disabled = t.scrollLeft >= max - 2;
    }
    step(dir) {
      const slides = [...this.track.querySelectorAll('[data-slide]')];
      const pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
      const current = this.track.scrollLeft + pad;
      let target;
      if (dir > 0) target = slides.find((s) => s.offsetLeft > current + 4);
      else target = [...slides].reverse().find((s) => s.offsetLeft < current - 4);
      const left = target ? target.offsetLeft - pad : (dir > 0 ? this.track.scrollWidth : 0);
      this.track.scrollTo({ left, behavior: scrollBehavior() });
    }
    bindDrag() {
      let down = false; let moved = false; let startX = 0; let startLeft = 0;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = false; startX = e.clientX; startLeft = this.track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 5) { moved = true; this.track.classList.add('is-dragging'); }
        if (moved) this.track.scrollLeft = startLeft - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (moved) {
          this.track.classList.remove('is-dragging');
          const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          this.track.addEventListener('click', swallow, { capture: true, once: true });
          setTimeout(() => this.track.removeEventListener('click', swallow, { capture: true }), 50);
          // settle onto the nearest card
          const slides = [...this.track.querySelectorAll('[data-slide]')];
          const pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
          const pos = this.track.scrollLeft + pad;
          const nearest = slides.reduce((a, s) => (Math.abs(s.offsetLeft - pos) < Math.abs(a.offsetLeft - pos) ? s : a), slides[0]);
          if (nearest) this.track.scrollTo({ left: nearest.offsetLeft - pad, behavior: scrollBehavior() });
        }
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* <character-builder>                                                 */
  /* ------------------------------------------------------------------ */
  class CharacterBuilder extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.querySelector('[data-builder-config]').textContent);
      } catch (e) { this.config = { base: [], pillars: {} }; }
      const clean = (list) => (list || []).map((s) => String(s).trim()).filter(Boolean);
      this.base = clean(this.config.base);
      this.pillarIdeas = {};
      Object.keys(this.config.pillars || {}).forEach((k) => { this.pillarIdeas[k] = clean(this.config.pillars[k]); });
      this.max = Number(this.dataset.max) || 3;
      this.target = Number(this.dataset.target) || 10;
      this.defaultName = this.dataset.defaultName || 'Mila';
      this.form = this.querySelector('[data-builder-form]');
      this.out = {};
      this.querySelectorAll('[data-out]').forEach((el) => { this.out[el.dataset.out] = el; });
      this.meter = this.querySelector('[data-meter]');
      this.limitEl = this.querySelector('[data-limit]');
      this.prevIdeas = [...(this.out.ideas ? this.out.ideas.children : [])].map((li) => li.textContent.trim());
      this.form.addEventListener('submit', (e) => e.preventDefault());
      this.form.addEventListener('input', (e) => this.onInput(e));
      this.form.addEventListener('change', (e) => this.onInput(e));
      this.render(null);
    }
    onInput(e) {
      const t = e.target;
      if (t.dataset.in === 'pillar' && e.type === 'change') {
        const checked = this.form.querySelectorAll('[data-in="pillar"]:checked');
        if (checked.length > this.max) {
          t.checked = false;
          const chip = t.closest('.chip');
          if (chip) { chip.classList.remove('is-shake'); void chip.offsetWidth; chip.classList.add('is-shake'); }
          if (this.limitEl) this.limitEl.textContent = this.dataset.limitText || '';
          return;
        }
        if (this.limitEl) this.limitEl.innerHTML = '&nbsp;';
      }
      if (e.type === 'input' && t.dataset.in !== 'name') return;
      this.render(t.dataset.in);
    }
    flash(key) {
      const el = this.out[key];
      if (!el || !motionOK()) return;
      el.classList.add('is-updated');
      raf(() => raf(() => el.classList.remove('is-updated')));
    }
    render(changed) {
      const f = this.form;
      const name = (f.querySelector('[data-in="name"]').value || '').trim() || this.defaultName;
      const cityInput = f.querySelector('[data-in="city"]:checked');
      const vibeInput = f.querySelector('[data-in="vibe"]:checked');
      const city = cityInput ? cityInput.value : '';
      const vibe = vibeInput ? vibeInput.value : '';
      const pillars = [...f.querySelectorAll('[data-in="pillar"]:checked')];

      if (this.out.name) this.out.name.textContent = name;
      if (this.out.city) this.out.city.textContent = city;
      if (this.out.vibe) this.out.vibe.textContent = vibe;
      if (this.out.pillars) this.out.pillars.textContent = pillars.map((p) => p.dataset.name).join(' · ') || '—';
      if (changed === 'pillar') this.flash('pillars'); else if (changed) this.flash(changed);

      const ideas = [];
      if (this.base[0]) ideas.push(this.base[0].replace(/\[city\]/g, city));
      pillars.forEach((p) => (this.pillarIdeas[p.value] || []).forEach((idea) => ideas.push(idea)));

      if (this.out.ideas) {
        const list = this.out.ideas;
        list.textContent = '';
        let n = 0;
        ideas.forEach((idea) => {
          const li = d.createElement('li');
          li.textContent = idea;
          if (changed && !this.prevIdeas.includes(idea)) { li.classList.add('is-new'); li.style.setProperty('--i', n++); }
          list.appendChild(li);
        });
        this.prevIdeas = ideas;
      }

      const count = ideas.length;
      const pass = count >= this.target;
      if (this.meter) {
        this.meter.style.setProperty('--p', Math.min(1, count / this.target).toFixed(3));
        this.meter.parentElement.classList.toggle('is-pass', pass);
      }
      if (this.out.score) {
        const base = (this.dataset.scoreTemplate || '').replace('[count]', count).replace('[target]', this.target);
        this.out.score.textContent = `${base} — ${pass ? this.dataset.pass : this.dataset.more}`;
      }
      const tpl = this.dataset.ctaTemplate;
      if (tpl) {
        const label = this.querySelector('.builder__cta [data-label]');
        if (label && !label.closest('[aria-busy="true"]')) label.textContent = tpl.replace(/\[name\]/g, name);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* <stage-tracker>                                                     */
  /* ------------------------------------------------------------------ */
  class StageTracker extends HTMLElement {
    connectedCallback() {
      this.panels = [...this.querySelectorAll('[data-stage]')];
      if (!this.panels.length) return;
      this.links = [...this.querySelectorAll('[data-stage-link]')];
      this.chips = [...this.querySelectorAll('[data-strip-item]')];
      this.strip = this.querySelector('[data-strip]');
      this.rail = this.querySelector('[data-rail]');
      this.stripRail = this.querySelector('[data-strip-rail]');
      this.list = this.querySelector('.stages__panels');
      this.active = -1;
      this.io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) this.setActive(this.panels.indexOf(e.target)); });
      }, { rootMargin: '-42% 0px -52% 0px', threshold: 0 });
      this.panels.forEach((p) => this.io.observe(p));
      this.onScroll = () => { if (!this.ticking) { this.ticking = true; raf(() => this.progress()); } };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.setActive(0);
      this.progress();
    }
    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }
    progress() {
      this.ticking = false;
      const r = this.list.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = clamp((vh * 0.5 - r.top) / Math.max(1, r.height), 0, 1);
      if (this.rail) this.rail.style.setProperty('--p', p.toFixed(3));
      if (this.stripRail) this.stripRail.style.setProperty('--p', p.toFixed(3));
    }
    setActive(i) {
      if (i < 0 || i === this.active) return;
      this.active = i;
      this.links.forEach((l, j) => {
        l.classList.toggle('is-active', j === i);
        if (j === i) l.setAttribute('aria-current', 'step'); else l.removeAttribute('aria-current');
      });
      this.chips.forEach((c, j) => {
        c.classList.toggle('is-active', j === i);
        c.classList.toggle('is-done', j < i);
      });
      const chip = this.chips[i];
      if (chip && this.strip && this.strip.offsetParent !== null) {
        this.strip.scrollTo({ left: chip.offsetLeft - 16, behavior: scrollBehavior() });
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* <prompt-preview> — accessible tabs                                  */
  /* ------------------------------------------------------------------ */
  class PromptPreview extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = this.tabs.map((t) => d.getElementById(t.getAttribute('aria-controls')));
      this.list = this.querySelector('[role="tablist"]');
      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.select(i)));
      this.list.addEventListener('keydown', (e) => {
        const i = this.tabs.indexOf(d.activeElement);
        if (i < 0) return;
        let next = null;
        if (e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
        if (e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
        if (e.key === 'Home') next = 0;
        if (e.key === 'End') next = this.tabs.length - 1;
        if (next !== null) { e.preventDefault(); this.select(next, true); }
      });
    }
    select(i, focus = false) {
      this.tabs.forEach((t, j) => {
        t.setAttribute('aria-selected', j === i ? 'true' : 'false');
        t.tabIndex = j === i ? 0 : -1;
      });
      this.panels.forEach((p, j) => {
        if (!p) return;
        p.hidden = j !== i;
        if (j === i) { p.classList.remove('is-entering'); void p.offsetWidth; p.classList.add('is-entering'); }
      });
      const tab = this.tabs[i];
      if (focus) tab.focus({ preventScroll: true });
      if (this.list.scrollWidth > this.list.clientWidth) {
        this.list.scrollTo({ left: tab.offsetLeft - 18, behavior: scrollBehavior() });
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* <tilt-card>                                                         */
  /* ------------------------------------------------------------------ */
  class TiltCard extends HTMLElement {
    connectedCallback() {
      if (!finePointer.matches || !motionOK()) return;
      this.inner = this.querySelector('.cover__inner') || this;
      this.addEventListener('pointermove', (e) => {
        const r = this.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        this.classList.add('is-active');
        this.inner.style.setProperty('--ry', `${(x * 16).toFixed(2)}deg`);
        this.inner.style.setProperty('--rx', `${(-y * 12).toFixed(2)}deg`);
        this.inner.style.setProperty('--gx', `${((x + 0.5) * 100).toFixed(1)}%`);
        this.inner.style.setProperty('--gy', `${((y + 0.5) * 100).toFixed(1)}%`);
      });
      this.addEventListener('pointerleave', () => {
        this.classList.remove('is-active');
        ['--ry', '--rx', '--gx', '--gy'].forEach((p) => this.inner.style.removeProperty(p));
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* <compare-slider>                                                    */
  /* ------------------------------------------------------------------ */
  class CompareSlider extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('[data-compare-range]');
      this.set(Number(this.range?.value || 50));
      this.range?.addEventListener('input', () => { this.touched = true; this.set(Number(this.range.value)); });
      let active = false;
      const fromEvent = (e) => {
        const r = this.getBoundingClientRect();
        return clamp(((e.clientX - r.left) / r.width) * 100, 0, 100);
      };
      let startX = 0; let startY = 0; let decided = false;
      this.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        active = true; decided = e.pointerType === 'mouse'; startX = e.clientX; startY = e.clientY;
        this.touched = true;
        if (decided) { this.setPointerCapture(e.pointerId); this.classList.add('is-dragging'); this.set(fromEvent(e)); }
      });
      this.addEventListener('pointermove', (e) => {
        if (!active) return;
        if (!decided) {
          const dx = Math.abs(e.clientX - startX); const dy = Math.abs(e.clientY - startY);
          if (dx < 6 && dy < 6) return;
          if (dy > dx) { active = false; return; }
          decided = true;
          this.setPointerCapture(e.pointerId);
          this.classList.add('is-dragging');
        }
        this.set(fromEvent(e));
      });
      const end = (e) => {
        if (active && !decided && e.type === 'pointerup') this.set(fromEvent(e));
        active = false; this.classList.remove('is-dragging');
      };
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);
      if (motionOK()) {
        const io = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting) { io.disconnect(); setTimeout(() => this.hint(), 500); }
        }, { threshold: 0.6 });
        io.observe(this);
      }
    }
    set(pos) {
      const p = clamp(Math.round(pos * 10) / 10, 0, 100);
      this.style.setProperty('--pos', `${p}%`);
      if (this.range) {
        this.range.value = String(Math.round(p));
        this.range.setAttribute('aria-valuetext', `${Math.round(p)}%`);
      }
    }
    hint() {
      if (this.touched) return;
      const keys = [50, 74, 30, 50];
      const seg = 520;
      const t0 = performance.now();
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const step = (now) => {
        if (this.touched) return;
        const el = now - t0;
        const k = Math.min(keys.length - 2, Math.floor(el / seg));
        const t = clamp((el - k * seg) / seg, 0, 1);
        this.set(keys[k] + (keys[k + 1] - keys[k]) * ease(t));
        if (el < seg * (keys.length - 1)) raf(step);
      };
      raf(step);
    }
  }

  /* ------------------------------------------------------------------ */
  /* <accordion-group> — animated <details>                              */
  /* ------------------------------------------------------------------ */
  class AccordionGroup extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('details').forEach((det) => {
        const summary = det.querySelector('summary');
        const body = det.querySelector('.acc__a');
        if (!summary || !body) return;
        summary.addEventListener('click', (e) => {
          if (!motionOK() || !body.animate) return;
          e.preventDefault();
          if (det._anim) det._anim.cancel();
          if (det.open) {
            const h = body.scrollHeight;
            det._anim = body.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 320, easing: 'cubic-bezier(0.65,0,0.35,1)' });
            det._anim.onfinish = () => { det.open = false; det._anim = null; };
          } else {
            det.open = true;
            const h = body.scrollHeight;
            det._anim = body.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 420, easing: 'cubic-bezier(0.16,1,0.3,1)' });
            det._anim.onfinish = () => { det._anim = null; };
          }
        });
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* <ticker-rotator> — announcement messages                            */
  /* ------------------------------------------------------------------ */
  class TickerRotator extends HTMLElement {
    connectedCallback() {
      this.items = [...this.children];
      if (this.items.length < 2 || !motionOK()) return;
      this.i = 0;
      this.timer = setInterval(() => {
        this.items[this.i].classList.remove('is-active');
        this.i = (this.i + 1) % this.items.length;
        this.items[this.i].classList.add('is-active');
      }, Number(this.dataset.interval) || 5000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }

  /* ------------------------------------------------------------------ */
  /* <localization-form>                                                 */
  /* ------------------------------------------------------------------ */
  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      this.querySelectorAll('select').forEach((s) => s.addEventListener('change', () => {
        s.setAttribute('aria-busy', 'true');
        form.submit();
      }));
    }
  }

  /* ------------------------------------------------------------------ */
  /* <variant-picker> (product page)                                     */
  /* ------------------------------------------------------------------ */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      try { this.variants = JSON.parse(this.querySelector('[data-variants]').textContent); } catch (e) { this.variants = []; }
      this.scope = this.closest('[data-product-scope]') || d;
      this.addEventListener('change', () => this.onChange());
    }
    onChange() {
      const selected = [...this.querySelectorAll('fieldset')].map((fs) => {
        const c = fs.querySelector('input:checked');
        return c ? c.value : null;
      });
      const variant = this.variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      this.apply(variant);
    }
    apply(variant) {
      const productId = this.dataset.productId;
      const buyers = d.querySelectorAll(`product-buy[data-product-id="${productId}"], [data-product-scope] product-buy, sticky-buy product-buy`);
      buyers.forEach((pb) => {
        const input = pb.querySelector('[name="id"]');
        const btn = pb.querySelector('[data-buy-button]');
        const label = pb.querySelector('[data-label]');
        if (!variant) {
          if (btn) btn.disabled = true;
          if (label) label.textContent = strings.unavailable;
          return;
        }
        if (input) input.value = variant.id;
        if (btn) btn.disabled = !variant.available;
        if (label) label.textContent = variant.available ? (label.dataset.defaultLabel || label.textContent) : strings.soldOut;
      });
      if (variant) {
        d.querySelectorAll('[data-price]').forEach((el) => {
          if (el.closest('[data-other-product]')) return;
          el.textContent = formatMoney(variant.price);
        });
        d.querySelectorAll('[data-compare-price]').forEach((el) => {
          const has = variant.compare_at_price && variant.compare_at_price > variant.price;
          el.hidden = !has;
          if (has) el.textContent = formatMoney(variant.compare_at_price);
        });
        if (window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
        live(formatMoney(variant.price));
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* <product-gallery> (product page)                                    */
  /* ------------------------------------------------------------------ */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.counter = this.querySelector('[data-gallery-current]');
      this.dialog = this.querySelector('dialog');
      this.current = 0;
      this.track.addEventListener('scroll', () => { if (!this.ticking) { this.ticking = true; raf(() => this.sync()); } }, { passive: true });
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.thumb))));
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.current - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.current + 1));
      this.querySelectorAll('[data-zoom]').forEach((b) => b.addEventListener('click', () => this.openZoom(Number(b.dataset.zoom))));
      if (this.dialog) {
        this.zoomTrack = this.dialog.querySelector('[data-zoom-track]');
        this.dialog.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.closeZoom());
        this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.closeZoom(); });
        this.dialog.addEventListener('close', () => {
          d.body.classList.remove('is-locked');
          if (this.lastTrigger) this.lastTrigger.focus({ preventScroll: true });
        });
      }
      this.sync();
    }
    goTo(i) {
      const n = this.slides.length;
      const idx = (i + n) % n;
      this.track.scrollTo({ left: this.slides[idx].offsetLeft, behavior: scrollBehavior() });
    }
    sync() {
      this.ticking = false;
      const w = this.track.clientWidth || 1;
      const i = clamp(Math.round(this.track.scrollLeft / w), 0, this.slides.length - 1);
      if (i === this.current && this.synced) return;
      this.synced = true;
      this.current = i;
      this.thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
      this.dots.forEach((dt, j) => dt.classList.toggle('is-active', j === i));
      if (this.counter) this.counter.textContent = String(i + 1);
      this.slides.forEach((s, j) => s.setAttribute('aria-hidden', j === i ? 'false' : 'true'));
    }
    openZoom(i) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.lastTrigger = d.activeElement;
      this.zoomTrack.querySelectorAll('img[data-src]').forEach((img) => {
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        img.removeAttribute('data-src');
      });
      d.body.classList.add('is-locked');
      this.dialog.showModal();
      const slide = this.zoomTrack.children[i];
      if (slide) this.zoomTrack.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
    }
    closeZoom() { if (this.dialog && this.dialog.open) this.dialog.close(); }
  }

  /* ------------------------------------------------------------------ */
  /* Register                                                            */
  /* ------------------------------------------------------------------ */
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  define('site-header', SiteHeader);
  define('menu-drawer', MenuDrawer);
  define('product-buy', ProductBuy);
  define('sticky-buy', StickyBuy);
  define('scene-deck', SceneDeck);
  define('ih-carousel', IhCarousel);
  define('character-builder', CharacterBuilder);
  define('stage-tracker', StageTracker);
  define('prompt-preview', PromptPreview);
  define('tilt-card', TiltCard);
  define('compare-slider', CompareSlider);
  define('accordion-group', AccordionGroup);
  define('ticker-rotator', TickerRotator);
  define('localization-form', LocalizationForm);
  define('variant-picker', VariantPicker);
  define('product-gallery', ProductGallery);

  const init = (scope = d) => {
    initReveal(scope);
    Parallax.observe(scope);
    initVideos(scope);
  };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Theme editor: re-initialise re-rendered sections and reveal everything immediately.
  d.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    e.target.querySelectorAll('[data-reveal], [data-split]').forEach((el) => el.classList.add('is-in'));
  });
  d.addEventListener('shopify:block:select', (e) => {
    const deck = e.target.closest('scene-deck');
    if (deck && deck.selectBlock) deck.selectBlock(e.target);
    const tab = e.target.closest('[role="tab"]');
    if (tab) tab.click();
    const slide = e.target.closest('[data-slide]');
    const track = slide && slide.closest('[data-track]');
    if (track) track.scrollTo({ left: slide.offsetLeft - 20, behavior: 'smooth' });
  });

  window.__themeReady = true;
  window.AIH = { formatMoney, updateCartCount };
})();
